import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import AdminShell from '../components/admin/AdminShell';
import { fetchAdminEngagementDashboard } from '../services/api/admin';
import type {
  AdminBreakdownPoint,
  AdminEngagementDashboard,
  AdminEngagementDashboardGranularity,
  AdminFlowDashboardMetrics,
} from '../types/admin';
import { formatDateTime } from '../utils/companyOwner';
import { chartName, chartNumber } from '../utils/chartValue';
import '../styles/register.css';
import '../styles/company-owner.css';
import '../styles/admin.css';

const GRANULARITIES: AdminEngagementDashboardGranularity[] = ['DAILY', 'WEEKLY', 'MONTHLY'];
const BREAKDOWN_COLORS = ['#0f766e', '#0f4fbf', '#d97706', '#b42318', '#64748b', '#7c3aed'];

function formatDateForInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function defaultFromDate(): string {
  const now = new Date();
  return formatDateForInput(new Date(now.getFullYear(), now.getMonth(), 1));
}

function defaultToDate(): string {
  return formatDateForInput(new Date());
}

function formatCompactNumber(value: number | null | undefined): string {
  return new Intl.NumberFormat('en-ZA', {
    maximumFractionDigits: 1,
    notation: Math.abs(Number(value ?? 0)) >= 10000 ? 'compact' : 'standard',
  }).format(Number(value ?? 0));
}

function formatPercent(value: number | null | undefined): string {
  return `${new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 1 }).format(Number(value ?? 0))}%`;
}

function formatCurrency(value: number | null | undefined, currency = 'ZAR'): string {
  return new Intl.NumberFormat('en-ZA', {
    currency: currency || 'ZAR',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    style: 'currency',
  }).format(Number(value ?? 0));
}

function normalizeLabel(value: string): string {
  return (value || 'UNKNOWN')
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

const flowRows = (metrics: AdminFlowDashboardMetrics) => [
  ['Requested', formatCompactNumber(metrics.requested)],
  ['Accepted', formatCompactNumber(metrics.accepted)],
  ['Not accepted', formatCompactNumber(metrics.notAccepted)],
  ['Completed', formatCompactNumber(metrics.completed)],
  ['Acceptance rate', formatPercent(metrics.acceptanceRatePercent)],
  ['Completion rate', formatPercent(metrics.completionRatePercent)],
  ['Still assigned', formatCompactNumber(metrics.stillAssigned)],
  ['Overdue', formatCompactNumber(metrics.overdue)],
];

const AdminEngagementDashboardPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [dashboard, setDashboard] = useState<AdminEngagementDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const filters = useMemo(
    () => ({
      fromDate: searchParams.get('fromDate') || defaultFromDate(),
      toDate: searchParams.get('toDate') || defaultToDate(),
      granularity: (searchParams.get('granularity') || 'DAILY') as AdminEngagementDashboardGranularity,
      includeDriverBreakdown: searchParams.get('includeDriverBreakdown') !== 'false',
    }),
    [searchParams],
  );

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (!next.get('fromDate')) {
      next.set('fromDate', filters.fromDate);
      changed = true;
    }
    if (!next.get('toDate')) {
      next.set('toDate', filters.toDate);
      changed = true;
    }
    if (!next.get('granularity')) {
      next.set('granularity', filters.granularity);
      changed = true;
    }
    if (!next.get('includeDriverBreakdown')) {
      next.set('includeDriverBreakdown', String(filters.includeDriverBreakdown));
      changed = true;
    }
    if (changed) {
      setSearchParams(next);
    }
  }, [filters, searchParams, setSearchParams]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetchAdminEngagementDashboard(filters);
        if (!mounted) return;
        setDashboard(response);
      } catch (loadError: any) {
        if (!mounted) return;
        setError(loadError.message || 'Could not load the engagement dashboard.');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, [filters]);

  const updateFilter = (key: string, value: string | boolean) => {
    const next = new URLSearchParams(searchParams);
    next.set(key, String(value));
    setSearchParams(next);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError('');
    try {
      setDashboard(await fetchAdminEngagementDashboard(filters));
    } catch (refreshError: any) {
      setError(refreshError.message || 'Could not refresh the engagement dashboard.');
    } finally {
      setRefreshing(false);
    }
  };

  const kpiValue = (key: string) => dashboard?.kpis.find((item) => item.key === key)?.value ?? 0;
  const currency = dashboard?.revenue.currency || 'ZAR';
  const revenueChartData = useMemo(() => {
    const periods = new Set([
      ...(dashboard?.revenueTrend ?? []).map((item) => item.period),
      ...(dashboard?.commissionTrend ?? []).map((item) => item.period),
    ]);

    return Array.from(periods).sort().map((period) => ({
      period,
      grossCaptured: dashboard?.revenueTrend.find((item) => item.period === period)?.value ?? 0,
      platformCommission: dashboard?.commissionTrend.find((item) => item.period === period)?.value ?? 0,
    }));
  }, [dashboard]);

  const riskRows = dashboard
    ? [
        ['Stale assigned bookings', dashboard.edgeCases.staleAssignedBookings],
        ['Searching past expiry', dashboard.edgeCases.searchingPastExpiry],
        ['Accepted without driver', dashboard.edgeCases.acceptedWithoutDriver],
        ['Completed without commission', dashboard.edgeCases.completedWithoutCommission],
        ['Captured but not completed', dashboard.edgeCases.capturedButNotCompleted],
        ['Cancelled after capture', dashboard.edgeCases.cancelledAfterCapture],
        ['Duplicate trip rows', dashboard.edgeCases.duplicateTripRows],
      ]
    : [];

  const hasBookingActivity = Boolean(
    dashboard?.bookingTrend.length || dashboard?.bookingsByStatus.length || dashboard?.bookingsByFlow.length,
  );

  return (
    <AdminShell
      badge="Engagement"
      copy="Track booking demand, conversion, revenue, enrolment, driver activity, and operational risk across the selected reporting window."
      title="Engagement dashboard"
    >
      <section className="admin-panel admin-engagement-toolbar-panel">
        <div className="admin-toolbar admin-engagement-toolbar">
          <label className="admin-filter-field">
            <span>From</span>
            <input
              className="register-input"
              onChange={(event) => updateFilter('fromDate', event.target.value)}
              type="date"
              value={filters.fromDate}
            />
          </label>
          <label className="admin-filter-field">
            <span>To</span>
            <input
              className="register-input"
              onChange={(event) => updateFilter('toDate', event.target.value)}
              type="date"
              value={filters.toDate}
            />
          </label>
          <label className="admin-filter-field">
            <span>Granularity</span>
            <select
              className="register-input"
              onChange={(event) => updateFilter('granularity', event.target.value)}
              value={filters.granularity}
            >
              {GRANULARITIES.map((granularity) => (
                <option key={granularity} value={granularity}>
                  {normalizeLabel(granularity)}
                </option>
              ))}
            </select>
          </label>
          <label className="admin-checkbox-row admin-engagement-checkbox">
            <input
              checked={filters.includeDriverBreakdown}
              onChange={(event) => updateFilter('includeDriverBreakdown', event.target.checked)}
              type="checkbox"
            />
            Driver breakdown
          </label>
          <button
            className="register-secondary-button admin-engagement-refresh"
            disabled={refreshing || loading}
            onClick={handleRefresh}
            type="button"
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </section>

      {error ? <div className="register-inline-error">{error}</div> : null}
      {dashboard?.warnings.length ? (
        <div className="admin-warning-list">
          {dashboard.warnings.map((warning) => (
            <div className="admin-message-alert" key={warning}>
              <span className="admin-message-alert-dot" />
              <span>{warning}</span>
            </div>
          ))}
        </div>
      ) : null}

      {loading ? (
        <div className="admin-panel">
          <div className="register-loader" />
        </div>
      ) : dashboard ? (
        <>
          <div className="admin-grid admin-engagement-kpi-grid">
            <div className="admin-panel admin-stat-card">
              <span className="admin-stat-label">Bookings made</span>
              <strong>{formatCompactNumber(kpiValue('totalBookings'))}</strong>
              <span className="admin-stat-note">All bookings created in the selected range.</span>
            </div>
            <div className="admin-panel admin-stat-card">
              <span className="admin-stat-label">Successful bookings</span>
              <strong>{formatCompactNumber(kpiValue('successfulBookings'))}</strong>
              <span className="admin-stat-note">Commercially successful delivered or completed trips.</span>
            </div>
            <div className="admin-panel admin-stat-card">
              <span className="admin-stat-label">Completed trips</span>
              <strong>{formatCompactNumber(kpiValue('completedTrips'))}</strong>
              <span className="admin-stat-note">Trips reaching the final completed state.</span>
            </div>
            <div className="admin-panel admin-stat-card">
              <span className="admin-stat-label">Gross captured</span>
              <strong>{formatCurrency(dashboard.revenue.grossCaptured, currency)}</strong>
              <span className="admin-stat-note">Captured revenue for completed or delivered trips.</span>
            </div>
            <div className="admin-panel admin-stat-card">
              <span className="admin-stat-label">Platform commission</span>
              <strong>{formatCurrency(dashboard.revenue.platformCommission, currency)}</strong>
              <span className="admin-stat-note">Commission retained by ShiftWagon.</span>
            </div>
            <div className="admin-panel admin-stat-card">
              <span className="admin-stat-label">Online drivers</span>
              <strong>{formatCompactNumber(dashboard.drivers.onlineNow)}</strong>
              <span className="admin-stat-note">Drivers currently online according to driver management.</span>
            </div>
          </div>

          <div className="admin-grid admin-section-grid">
            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Instant requests</h2>
                  <p className="admin-panel-copy">Demand and conversion for instant booking flow.</p>
                </div>
              </div>
              <div className="admin-metric-grid">
                {flowRows(dashboard.instantRequests).map(([label, value]) => (
                  <div className="admin-detail-card" key={label}>
                    <span>{label}</span>
                    <strong>{value}</strong>
                  </div>
                ))}
              </div>
            </section>

            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Assignment bookings</h2>
                  <p className="admin-panel-copy">Acceptance health for bookings dispatched to assigned drivers.</p>
                </div>
              </div>
              <div className="admin-metric-grid">
                {flowRows(dashboard.assignmentBookings).map(([label, value]) => (
                  <div className="admin-detail-card" key={label}>
                    <span>{label}</span>
                    <strong>{value}</strong>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <section className="admin-panel">
            <div className="admin-panel-header">
              <div>
                <h2 className="admin-panel-title">Revenue</h2>
                <p className="admin-panel-copy">
                  Gross captured and platform commission over time. Driver payouts total {formatCurrency(dashboard.revenue.driverPayout, currency)}.
                </p>
              </div>
              <span className="admin-pill">Avg commission {formatCurrency(dashboard.revenue.averageCommissionPerCompletedTrip, currency)}</span>
            </div>
            {revenueChartData.length ? (
              <div className="admin-chart-frame">
                <ResponsiveContainer height="100%" width="100%">
                  <AreaChart data={revenueChartData} margin={{ bottom: 8, left: 0, right: 12, top: 12 }}>
                    <CartesianGrid stroke="#dbe7f6" strokeDasharray="3 3" />
                    <XAxis dataKey="period" minTickGap={24} tick={{ fill: '#5e7395', fontSize: 12 }} />
                    <YAxis tick={{ fill: '#5e7395', fontSize: 12 }} tickFormatter={(value) => formatCompactNumber(Number(value))} />
                    <Tooltip formatter={(value) => formatCurrency(chartNumber(value), currency)} />
                    <Legend />
                    <Area dataKey="grossCaptured" fill="#0f766e33" name="Gross captured" stroke="#0f766e" strokeWidth={2} type="monotone" />
                    <Area dataKey="platformCommission" fill="#0f4fbf2e" name="Platform commission" stroke="#0f4fbf" strokeWidth={2} type="monotone" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="admin-empty">No revenue trend points are available for this range.</p>
            )}
          </section>

          <div className="admin-grid admin-grid-main">
            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Booking activity</h2>
                  <p className="admin-panel-copy">Booking volume by selected reporting granularity.</p>
                </div>
              </div>
              {dashboard.bookingTrend.length ? (
                <div className="admin-chart-frame">
                  <ResponsiveContainer height="100%" width="100%">
                    <BarChart data={dashboard.bookingTrend} margin={{ bottom: 8, left: 0, right: 12, top: 12 }}>
                      <CartesianGrid stroke="#dbe7f6" strokeDasharray="3 3" />
                      <XAxis dataKey="period" minTickGap={24} tick={{ fill: '#5e7395', fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fill: '#5e7395', fontSize: 12 }} />
                      <Tooltip formatter={(value) => formatCompactNumber(chartNumber(value))} />
                      <Bar dataKey="value" fill="#0f4fbf" name="Bookings" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="admin-empty">No booking activity points are available for this range.</p>
              )}
            </section>

            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Booking mix</h2>
                  <p className="admin-panel-copy">Status and flow composition for the selected range.</p>
                </div>
              </div>
              {hasBookingActivity ? (
                <>
                  <div className="admin-chart-frame admin-chart-frame-small">
                    <ResponsiveContainer height="100%" width="100%">
                      <PieChart>
                        <Pie data={dashboard.bookingsByStatus} dataKey="value" innerRadius={54} nameKey="label" outerRadius={88} paddingAngle={2}>
                          {dashboard.bookingsByStatus.map((entry, index) => (
                            <Cell fill={BREAKDOWN_COLORS[index % BREAKDOWN_COLORS.length]} key={entry.label} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => [
                            formatCompactNumber(chartNumber(value)),
                            normalizeLabel(chartName(name)),
                          ]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <BreakdownList points={dashboard.bookingsByStatus} />
                  <div className="admin-pill-row">
                    {dashboard.bookingsByFlow.map((item) => (
                      <span className="admin-pill" key={item.label}>
                        {normalizeLabel(item.label)}: {formatCompactNumber(item.value)}
                      </span>
                    ))}
                  </div>
                </>
              ) : (
                <p className="admin-empty">No booking breakdown is available for this range.</p>
              )}
            </section>
          </div>

          <section className="admin-panel">
            <div className="admin-panel-header">
              <div>
                <h2 className="admin-panel-title">Enrolment</h2>
                <p className="admin-panel-copy">Customer and driver onboarding health for the selected range.</p>
              </div>
            </div>
            <div className="admin-section-grid">
              <div className="admin-metric-grid">
                {[
                  ['Users enrolled', dashboard.users.enrolled],
                  ['Users active', dashboard.users.active],
                  ['Completed onboarding', dashboard.users.completedOnboarding],
                  ['Still onboarding', dashboard.users.stillOnboarding],
                  ['Users deleted', dashboard.users.deleted],
                ].map(([label, value]) => (
                  <div className="admin-detail-card" key={label}>
                    <span>{label}</span>
                    <strong>{formatCompactNumber(Number(value))}</strong>
                  </div>
                ))}
              </div>
              <div className="admin-metric-grid">
                {[
                  ['Drivers enrolled', dashboard.drivers.enrolled],
                  ['Drivers active', dashboard.drivers.active],
                  ['Successfully onboarded', dashboard.drivers.successfullyOnboarded],
                  ['Still onboarding', dashboard.drivers.stillOnboarding],
                  ['Online now', dashboard.drivers.onlineNow],
                  ['Drivers deleted', dashboard.drivers.deleted],
                ].map(([label, value]) => (
                  <div className="admin-detail-card" key={label}>
                    <span>{label}</span>
                    <strong>{formatCompactNumber(Number(value))}</strong>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {filters.includeDriverBreakdown ? (
            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Driver revenue</h2>
                  <p className="admin-panel-copy">Top drivers by platform commission, capped by the backend for operational scanning.</p>
                </div>
              </div>
              {dashboard.driverRevenue.length ? (
                <div className="admin-table admin-table-driver-revenue">
                  <div className="admin-table-head admin-table-head-driver-revenue">
                    <span>Driver</span>
                    <span>Trips</span>
                    <span>Gross captured</span>
                    <span>Driver payouts</span>
                    <span>Platform commission</span>
                  </div>
                  {dashboard.driverRevenue.map((row) => (
                    <div className="admin-table-row admin-table-row-driver-revenue" key={row.driverId}>
                      <span>
                        <strong>{row.driverName || 'Driver name unavailable'}</strong>
                        <small>{row.driverId}</small>
                      </span>
                      <span>{formatCompactNumber(row.completedTrips)}</span>
                      <span>{formatCurrency(row.grossCaptured, currency)}</span>
                      <span>{formatCurrency(row.driverPayout, currency)}</span>
                      <span>{formatCurrency(row.platformCommission, currency)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="admin-empty">No driver revenue rows are available for this range.</p>
              )}
            </section>
          ) : null}

          <section className="admin-panel">
            <div className="admin-panel-header">
              <div>
                <h2 className="admin-panel-title">Edge cases</h2>
                <p className="admin-panel-copy">Operational risk indicators that need follow-up when non-zero.</p>
              </div>
              <span className="admin-muted-note">Generated {formatDateTime(dashboard.generatedAt)}</span>
            </div>
            <div className="admin-risk-grid">
              {riskRows.map(([label, value]) => (
                <div className={`admin-detail-card ${Number(value) > 0 ? 'admin-risk-card-active' : ''}`} key={label}>
                  <span>{label}</span>
                  <strong>{formatCompactNumber(Number(value))}</strong>
                </div>
              ))}
            </div>
          </section>
        </>
      ) : (
        <div className="admin-panel">
          <p className="admin-empty">No engagement dashboard data is available.</p>
        </div>
      )}
    </AdminShell>
  );
};

const BreakdownList: React.FC<{ points: AdminBreakdownPoint[] }> = ({ points }) => (
  <div className="admin-breakdown-list">
    {points.map((item, index) => (
      <div className="admin-breakdown-row" key={item.label}>
        <span>
          <i style={{ background: BREAKDOWN_COLORS[index % BREAKDOWN_COLORS.length] }} />
          {normalizeLabel(item.label)}
        </span>
        <strong>{formatCompactNumber(item.value)}</strong>
      </div>
    ))}
  </div>
);

export default AdminEngagementDashboardPage;
